"use client";

import { useRef, useState, useSyncExternalStore } from "react";
import { AnimatePresence, motion, useReducedMotion, type PanInfo } from "framer-motion";
import { cn } from "../../lib/utils";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetMotionContent,
  SheetMotionOverlay,
  SheetPortal,
  SheetTitle,
} from "./sheet";
import {
  BOTTOM_SHEET_FALLBACK_TITLE,
  DETAILS_SHEET_CLASS_NAME,
  DETAILS_SHEET_SCROLL_CLASS_NAME,
  DETAILS_SHEET_SPRING,
  DETAILS_SHEET_COLLAPSED_RATIO,
  DETAILS_SHEET_MIN_HEIGHT_RATIO,
  DETAILS_SHEET_MIN_HEIGHT,
  DETAILS_SHEET_CLOSE_HEIGHT_RATIO,
  DETAILS_SHEET_EXPAND_DRAG_THRESHOLD,
  DETAILS_SHEET_CLOSE_DRAG_THRESHOLD,
  DETAILS_SHEET_DRAG_ELASTIC,
} from "./bottom-sheet.constants";
import type { BottomSheetProps } from "./bottom-sheet.types";

export type { BottomSheetProps } from "./bottom-sheet.types";

function getWindowHeight() {
  return window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
}

function getServerWindowHeight() {
  return 0;
}

function subscribeToWindowHeight(onResize: () => void) {
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}

function DetailsSheetPanel({
  onClose,
  children,
  title,
  headerAction,
  showDragHandle = true,
  hideTitle,
  className,
  contentClassName,
  overlayClassName,
  footer,
  onScroll,
  id,
  "data-experiment-id": experimentId,
}: BottomSheetProps) {
  const windowHeight = useSyncExternalStore(
    subscribeToWindowHeight,
    getWindowHeight,
    getServerWindowHeight,
  );
  const reducedMotion = useReducedMotion();
  const [isFullHeight, setIsFullHeight] = useState(false);
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const collapsedHeight = windowHeight * DETAILS_SHEET_COLLAPSED_RATIO;
  const minHeight = Math.min(
    Math.max(windowHeight * DETAILS_SHEET_MIN_HEIGHT_RATIO, DETAILS_SHEET_MIN_HEIGHT),
    windowHeight,
  );
  const currentHeight = dragHeight ?? (isFullHeight ? windowHeight : collapsedHeight);
  const dragStartHeight = useRef(currentHeight);
  const returnFocus = useRef<HTMLElement | null>(null);
  const targetTop = Math.max(windowHeight - currentHeight, 0);

  const handleDrag = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setDragHeight(Math.min(Math.max(dragStartHeight.current - info.offset.y, minHeight), windowHeight));
  };

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.y < DETAILS_SHEET_EXPAND_DRAG_THRESHOLD) {
      setIsFullHeight(true);
    } else if (
      info.offset.y > DETAILS_SHEET_CLOSE_DRAG_THRESHOLD ||
      currentHeight <= windowHeight * DETAILS_SHEET_CLOSE_HEIGHT_RATIO
    ) {
      onClose();
      return;
    } else {
      setIsFullHeight(false);
    }
    setDragHeight(null);
  };

  return (
    <>
      <SheetMotionOverlay
        forceMount
        className={cn("fixed inset-0 z-50 bg-black/40", overlayClassName)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={reducedMotion ? { duration: 0 } : undefined}
      />
      <SheetMotionContent
        forceMount
        id={id}
        data-experiment-id={experimentId}
        onOpenAutoFocus={() => {
          returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }}
        onCloseAutoFocus={(event) => {
          if (returnFocus.current?.isConnected) {
            event.preventDefault();
            returnFocus.current.focus();
          }
        }}
        className={cn(DETAILS_SHEET_CLASS_NAME, className, contentClassName)}
        initial={reducedMotion ? false : { top: windowHeight }}
        animate={{ top: targetTop, height: currentHeight, maxHeight: currentHeight }}
        exit={{ top: windowHeight }}
        transition={reducedMotion ? { duration: 0 } : DETAILS_SHEET_SPRING}
        style={{ height: currentHeight, maxHeight: currentHeight }}
      >
        {showDragHandle ? (
          <motion.button
            type="button"
            aria-label={isFullHeight ? "Collapse sheet" : "Expand sheet"}
            aria-expanded={isFullHeight}
            className="flex h-10 shrink-0 touch-none cursor-grab items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0D41E1] active:cursor-grabbing"
            onClick={() => {
              setDragHeight(null);
              setIsFullHeight((value) => !value);
            }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={DETAILS_SHEET_DRAG_ELASTIC}
            onDragStart={() => { dragStartHeight.current = currentHeight; }}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
          >
            <span className="h-0.5 w-9 rounded-full bg-[#98A2B3]" />
          </motion.button>
        ) : null}
        {(title && !hideTitle) || headerAction ? (
          <div className="mb-4 flex shrink-0 items-center justify-between gap-3 px-3">
            <SheetTitle className={cn("text-lg font-semibold text-[#344054]", (!title || hideTitle) && "sr-only")}>
              {title ?? BOTTOM_SHEET_FALLBACK_TITLE}
            </SheetTitle>
            {headerAction}
          </div>
        ) : (
          <SheetTitle className="sr-only">{title ?? BOTTOM_SHEET_FALLBACK_TITLE}</SheetTitle>
        )}
        <SheetDescription className="sr-only">Additional information and actions.</SheetDescription>
        <div className={DETAILS_SHEET_SCROLL_CLASS_NAME} style={{ scrollbarGutter: "stable" }} onScroll={onScroll}>
          {children}
        </div>
        {footer}
      </SheetMotionContent>
    </>
  );
}

export function BottomSheet(props: BottomSheetProps) {
  const {
    isOpen,
    onClose,
    children,
    title,
    headerAction,
    showDragHandle = true,
    className,
    overlayClassName,
    contentClassName,
    desktopPlacement = "center",
    variant = "default",
    contentKey,
  } = props;
  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  if (variant === "details") {
    return (
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetPortal forceMount>
          <AnimatePresence mode="wait">
            {isOpen ? <DetailsSheetPanel key={contentKey} {...props} /> : null}
          </AnimatePresence>
        </SheetPortal>
      </Sheet>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        className={cn(
          "fixed! bottom-0! border-none bg-transparent p-0 shadow-none focus:outline-none md:fixed! md:left-1/2 md:right-auto! md:w-full md:max-w-md md:-translate-x-1/2",
          desktopPlacement === "bottom"
            ? "md:bottom-0! md:top-auto! md:translate-y-0"
            : "md:bottom-auto! md:top-1/2 md:-translate-y-1/2",
          contentClassName,
        )}
        overlayClassName={overlayClassName}
      >
        <div
          className={cn(
            "mx-auto w-full max-w-md rounded-t-2xl max-h-[70vh] md:max-h-[90dvh] overflow-y-auto overflow-x-hidden bg-white px-4 pb-6 pt-3 md:shadow-2xl",
            desktopPlacement === "center" && "md:rounded-2xl",
            className,
          )}
        >
          {showDragHandle && (
            <div
              className={cn(
                "mb-4 flex justify-center",
                desktopPlacement === "center" && "md:hidden",
              )}
            >
              <div className="h-[3px] w-12 rounded-full bg-[#B7B5AF]" />
            </div>
          )}

          {title || headerAction ? (
            <div className="mb-4 flex items-center justify-between gap-3">
              <SheetTitle
                className={cn(
                  "text-lg font-semibold text-[#344054]",
                  !title && "sr-only",
                )}
              >
                {title ?? BOTTOM_SHEET_FALLBACK_TITLE}
              </SheetTitle>
              {headerAction}
            </div>
          ) : (
            <SheetTitle className="sr-only">{BOTTOM_SHEET_FALLBACK_TITLE}</SheetTitle>
          )}
          <SheetDescription className="sr-only">
            Additional information and actions.
          </SheetDescription>

          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
